import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { 
  Clock, 
  ArrowLeft, 
  Eye, 
  EyeOff, 
  Building2, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  FileText,
  Upload,
  CheckCircle,
  Search,
  Loader2
} from "lucide-react";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Dados da Empresa
    razao_social: "",
    nome_fantasia: "",
    cnpj: "",
    logo_url: "",
    // Plano selecionado
    plano: "basico",
    // Endereço
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    // Contato
    telefone: "",
    celular: "",
    email_empresa: "",
    website: "",
    // Dados do Administrador
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    // Termos
    aceite_termos: false,
    aceite_privacidade: false
  });
  
  const planos = [
    { id: "basico", nome: "Básico", preco: 99, descricao: "Até 7 funcionários", recursos: ["Registro de ponto", "Relatórios básicos", "Suporte WhatsApp"] },
    { id: "profissional", nome: "Profissional", preco: 299, descricao: "Até 30 funcionários", recursos: ["Tudo do Básico", "Geolocalização", "Cerca Eletrônica", "Suporte prioritário"] },
    { id: "enterprise", nome: "Enterprise", preco: 599, descricao: "Funcionários ilimitados", recursos: ["Tudo do Profissional", "API personalizada", "Gerente de conta"] }
  ];
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCNPJ, setLoadingCNPJ] = useState(false);
  const [cnpjError, setCnpjError] = useState("");

  // Buscar dados do CNPJ na API BrasilAPI
  const buscarCNPJ = async () => {
    const cnpjLimpo = formData.cnpj.replace(/\D/g, "");
    
    if (cnpjLimpo.length !== 14) {
      toast.error("CNPJ inválido. Digite os 14 dígitos.");
      return;
    }

    setLoadingCNPJ(true);
    setCnpjError("");

    try {
      const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`);
      
      if (!response.ok) {
        throw new Error("CNPJ não encontrado");
      }

      const data = await response.json();
      
      // Preencher os campos com os dados retornados
      setFormData(prev => ({
        ...prev,
        razao_social: data.razao_social || "",
        nome_fantasia: data.nome_fantasia || "",
        email_empresa: data.email || prev.email_empresa,
        telefone: data.ddd_telefone_1 ? `(${data.ddd_telefone_1.substring(0,2)}) ${data.ddd_telefone_1.substring(2)}` : prev.telefone,
        // Endereço
        cep: data.cep ? data.cep.replace(/(\d{5})(\d{3})/, "$1-$2") : prev.cep,
        endereco: data.logradouro || prev.endereco,
        numero: data.numero || prev.numero,
        complemento: data.complemento || prev.complemento,
        bairro: data.bairro || prev.bairro,
        cidade: data.municipio || prev.cidade,
        estado: data.uf || prev.estado
      }));

      toast.success("Dados do CNPJ carregados com sucesso!");
    } catch (error) {
      setCnpjError("Não foi possível encontrar os dados do CNPJ. Verifique se está correto ou preencha manualmente.");
      toast.error("CNPJ não encontrado ou inválido");
    } finally {
      setLoadingCNPJ(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === "checkbox" ? checked : value 
    });
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData({ ...formData, [name]: checked });
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, "");
    return numbers.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, "");
    return numbers.replace(/^(\d{5})(\d{3})$/, "$1-$2");
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3");
    }
    return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  };

  const handleCNPJChange = (e) => {
    const formatted = formatCNPJ(e.target.value);
    setFormData({ ...formData, cnpj: formatted });
  };

  const handleCEPChange = (e) => {
    const formatted = formatCEP(e.target.value);
    setFormData({ ...formData, cep: formatted });
  };

  const handlePhoneChange = (name, value) => {
    const formatted = formatPhone(value);
    setFormData({ ...formData, [name]: formatted });
  };

  const validateStep1 = () => {
    if (!formData.razao_social || !formData.cnpj || !formData.email_empresa) {
      toast.error("Preencha os campos obrigatórios da empresa");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.cep || !formData.endereco || !formData.cidade || !formData.estado) {
      toast.error("Preencha os campos obrigatórios do endereço");
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Preencha os dados do administrador");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("As senhas não coincidem");
      return false;
    }
    if (formData.password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return false;
    }
    if (!formData.aceite_termos || !formData.aceite_privacidade) {
      toast.error("Você precisa aceitar os termos para continuar");
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep3()) return;

    setLoading(true);

    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        company_name: formData.razao_social,  // Razão Social
        company_trade_name: formData.nome_fantasia || null,  // Nome Fantasia
        company_cnpj: formData.cnpj,
        company_logo: formData.logo_url,
        // Plano escolhido
        company_plan: formData.plano,
        // Dados de endereço para geração de boletos
        company_cep: formData.cep?.replace(/\D/g, ''),
        company_address: formData.endereco,
        company_number: formData.numero,
        company_bairro: formData.bairro,
        company_city: formData.cidade,
        company_state: formData.estado,
        company_phone: formData.telefone?.replace(/\D/g, ''),
        role: "admin"
      });
      toast.success(`Conta criada com sucesso! Bem-vindo, ${user.name}!`);
      navigate("/admin");
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao criar conta";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: "Dados da Empresa" },
    { number: 2, title: "Endereço" },
    { number: 3, title: "Administrador" }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="p-4 border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Voltar ao início
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-sky-500 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-lg text-slate-900">CiaPonto</span>
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="font-heading font-bold text-2xl text-slate-900">Cadastro de Empresa</h1>
            <p className="text-slate-600">Preencha os dados para criar sua conta</p>
          </div>

          {/* Steps indicator */}
          <div className="flex items-center justify-center mb-8">
            {steps.map((s, index) => (
              <div key={s.number} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm transition-all ${
                  step >= s.number 
                    ? "bg-sky-500 text-white" 
                    : "bg-slate-200 text-slate-500"
                }`}>
                  {step > s.number ? <CheckCircle className="w-5 h-5" /> : s.number}
                </div>
                <span className={`ml-2 text-sm font-medium hidden sm:inline ${
                  step >= s.number ? "text-sky-600" : "text-slate-400"
                }`}>
                  {s.title}
                </span>
                {index < steps.length - 1 && (
                  <div className={`w-12 sm:w-20 h-1 mx-2 rounded ${
                    step > s.number ? "bg-sky-500" : "bg-slate-200"
                  }`} />
                )}
              </div>
            ))}
          </div>

          <Card className="border border-slate-200 shadow-lg">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                {step === 1 && <><Building2 className="w-5 h-5 text-sky-500" /> Dados da Empresa</>}
                {step === 2 && <><MapPin className="w-5 h-5 text-sky-500" /> Endereço</>}
                {step === 3 && <><User className="w-5 h-5 text-sky-500" /> Administrador e Termos</>}
              </CardTitle>
              <CardDescription>
                {step === 1 && "Informações cadastrais da empresa"}
                {step === 2 && "Endereço completo da empresa"}
                {step === 3 && "Dados de acesso e aceite dos termos"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit}>
                {/* Step 1: Dados da Empresa */}
                {step === 1 && (
                  <div className="space-y-4">
                    {/* CNPJ - Primeiro campo com busca automática */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        CNPJ *
                        {loadingCNPJ && <span className="text-xs text-sky-500 animate-pulse">Buscando dados...</span>}
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          name="cnpj"
                          placeholder="00.000.000/0000-00"
                          value={formData.cnpj}
                          onChange={handleCNPJChange}
                          maxLength={18}
                          required
                          className="flex-1"
                          data-testid="cnpj-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={buscarCNPJ}
                          disabled={loadingCNPJ || formData.cnpj.replace(/\D/g, "").length !== 14}
                          className="px-4"
                        >
                          {loadingCNPJ ? "..." : "Buscar"}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">Digite o CNPJ e clique em Buscar para preencher automaticamente</p>
                    </div>

                    {cnpjError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">{cnpjError}</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Razão Social *</Label>
                      <Input
                        name="razao_social"
                        placeholder="Razão Social da Empresa"
                        value={formData.razao_social}
                        onChange={handleChange}
                        required
                        data-testid="razao-social-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Nome Fantasia</Label>
                      <Input
                        name="nome_fantasia"
                        placeholder="Nome Fantasia (opcional)"
                        value={formData.nome_fantasia}
                        onChange={handleChange}
                        data-testid="nome-fantasia-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email da Empresa *</Label>
                        <Input
                          name="email_empresa"
                          type="email"
                          placeholder="contato@empresa.com"
                          value={formData.email_empresa}
                          onChange={handleChange}
                          required
                          data-testid="email-empresa-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Website</Label>
                        <Input
                          name="website"
                          placeholder="www.empresa.com.br"
                          value={formData.website}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Telefone</Label>
                        <Input
                          name="telefone"
                          placeholder="(00) 0000-0000"
                          value={formData.telefone}
                          onChange={(e) => handlePhoneChange("telefone", e.target.value)}
                          maxLength={14}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Celular/WhatsApp</Label>
                        <Input
                          name="celular"
                          placeholder="(00) 00000-0000"
                          value={formData.celular}
                          onChange={(e) => handlePhoneChange("celular", e.target.value)}
                          maxLength={15}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Logo da Empresa</Label>
                      <div className="flex items-center gap-4">
                        {formData.logo_url ? (
                          <div className="relative">
                            <img 
                              src={formData.logo_url} 
                              alt="Logo" 
                              className="w-20 h-20 object-contain border rounded-lg bg-white"
                            />
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, logo_url: "" })}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50">
                            <Upload className="w-6 h-6 text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                // Converter para base64 e enviar para Cloudflare Images
                                const reader = new FileReader();
                                reader.onload = async () => {
                                  try {
                                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload-logo`, {
                                      method: "POST",
                                      headers: { "Content-Type": "application/json" },
                                      body: JSON.stringify({ image: reader.result })
                                    });
                                    const data = await response.json();
                                    if (data.success) {
                                      setFormData({ ...formData, logo_url: data.image_url });
                                      toast.success("Logo enviada com sucesso!");
                                    } else {
                                      toast.error(data.detail || "Erro ao enviar logo");
                                    }
                                  } catch (error) {
                                    toast.error("Erro ao enviar logo");
                                  }
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="hidden"
                            id="logo-upload"
                          />
                          <label
                            htmlFor="logo-upload"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg cursor-pointer transition-colors text-sm font-medium text-slate-700"
                          >
                            <Upload className="w-4 h-4" />
                            {formData.logo_url ? "Trocar Logo" : "Escolher Logo"}
                          </label>
                          <p className="text-xs text-slate-500 mt-1">JPG, PNG, GIF ou WEBP (máx. 2MB)</p>
                        </div>
                      </div>
                    </div>

                    {/* Seleção de Plano */}
                    <div className="space-y-3 pt-4 border-t">
                      <Label className="text-base font-medium">Escolha seu Plano *</Label>
                      <p className="text-sm text-slate-500">Você terá 7 dias grátis para testar. Após o período, será cobrado conforme o plano escolhido.</p>
                      <div className="grid gap-3">
                        {planos.map((plano) => (
                          <div
                            key={plano.id}
                            onClick={() => setFormData({ ...formData, plano: plano.id })}
                            className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all ${
                              formData.plano === plano.id
                                ? "border-sky-500 bg-sky-50"
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  formData.plano === plano.id ? "border-sky-500" : "border-slate-300"
                                }`}>
                                  {formData.plano === plano.id && (
                                    <div className="w-3 h-3 rounded-full bg-sky-500" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">{plano.nome}</p>
                                  <p className="text-sm text-slate-500">{plano.descricao}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-lg text-slate-900">R$ {plano.preco}</p>
                                <p className="text-xs text-slate-500">/mês</p>
                              </div>
                            </div>
                            {formData.plano === plano.id && (
                              <div className="mt-3 pt-3 border-t border-sky-200">
                                <ul className="text-sm text-slate-600 space-y-1">
                                  {plano.recursos.map((recurso, idx) => (
                                    <li key={idx} className="flex items-center gap-2">
                                      <CheckCircle className="w-4 h-4 text-green-500" />
                                      {recurso}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Endereço */}
                {step === 2 && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>CEP *</Label>
                        <Input
                          name="cep"
                          placeholder="00000-000"
                          value={formData.cep}
                          onChange={handleCEPChange}
                          maxLength={9}
                          required
                          data-testid="cep-input"
                        />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Endereço *</Label>
                        <Input
                          name="endereco"
                          placeholder="Rua, Avenida..."
                          value={formData.endereco}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Número *</Label>
                        <Input
                          name="numero"
                          placeholder="Nº"
                          value={formData.numero}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Complemento</Label>
                        <Input
                          name="complemento"
                          placeholder="Sala, Andar, Bloco..."
                          value={formData.complemento}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Bairro *</Label>
                      <Input
                        name="bairro"
                        placeholder="Bairro"
                        value={formData.bairro}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Cidade *</Label>
                        <Input
                          name="cidade"
                          placeholder="Cidade"
                          value={formData.cidade}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Estado *</Label>
                        <select
                          name="estado"
                          className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                          value={formData.estado}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Selecione...</option>
                          <option value="AC">Acre</option>
                          <option value="AL">Alagoas</option>
                          <option value="AP">Amapá</option>
                          <option value="AM">Amazonas</option>
                          <option value="BA">Bahia</option>
                          <option value="CE">Ceará</option>
                          <option value="DF">Distrito Federal</option>
                          <option value="ES">Espírito Santo</option>
                          <option value="GO">Goiás</option>
                          <option value="MA">Maranhão</option>
                          <option value="MT">Mato Grosso</option>
                          <option value="MS">Mato Grosso do Sul</option>
                          <option value="MG">Minas Gerais</option>
                          <option value="PA">Pará</option>
                          <option value="PB">Paraíba</option>
                          <option value="PR">Paraná</option>
                          <option value="PE">Pernambuco</option>
                          <option value="PI">Piauí</option>
                          <option value="RJ">Rio de Janeiro</option>
                          <option value="RN">Rio Grande do Norte</option>
                          <option value="RS">Rio Grande do Sul</option>
                          <option value="RO">Rondônia</option>
                          <option value="RR">Roraima</option>
                          <option value="SC">Santa Catarina</option>
                          <option value="SP">São Paulo</option>
                          <option value="SE">Sergipe</option>
                          <option value="TO">Tocantins</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Administrador e Termos */}
                {step === 3 && (
                  <div className="space-y-4">
                    <div className="p-4 bg-sky-50 rounded-lg border border-sky-100 mb-4">
                      <p className="text-sm text-sky-700">
                        <strong>Dados do Administrador:</strong> Esta pessoa terá acesso total ao sistema para gerenciar funcionários e relatórios.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Nome Completo do Administrador *</Label>
                      <Input
                        name="name"
                        placeholder="Nome completo"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="admin-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Email de Acesso *</Label>
                      <Input
                        name="email"
                        type="email"
                        placeholder="admin@empresa.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="admin-email-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Senha *</Label>
                        <div className="relative">
                          <Input
                            name="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Mínimo 6 caracteres"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            data-testid="admin-password-input"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Confirmar Senha *</Label>
                        <Input
                          name="confirmPassword"
                          type={showPassword ? "text" : "password"}
                          placeholder="Confirme a senha"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          required
                          data-testid="admin-confirm-password-input"
                        />
                      </div>
                    </div>

                    {/* Termos */}
                    <div className="border-t pt-4 mt-6 space-y-4">
                      <h4 className="font-medium text-slate-900 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Termos e Condições
                      </h4>
                      
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="aceite_termos"
                          checked={formData.aceite_termos}
                          onCheckedChange={(checked) => handleCheckboxChange("aceite_termos", checked)}
                          data-testid="aceite-termos-checkbox"
                        />
                        <label htmlFor="aceite_termos" className="text-sm text-slate-600 cursor-pointer">
                          Li e aceito os{" "}
                          <a href="#" className="text-sky-600 hover:underline" onClick={(e) => { e.preventDefault(); toast.info("Termos de uso do sistema CiaPonto"); }}>
                            Termos de Uso
                          </a>{" "}
                          do sistema CiaPonto *
                        </label>
                      </div>

                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="aceite_privacidade"
                          checked={formData.aceite_privacidade}
                          onCheckedChange={(checked) => handleCheckboxChange("aceite_privacidade", checked)}
                          data-testid="aceite-privacidade-checkbox"
                        />
                        <label htmlFor="aceite_privacidade" className="text-sm text-slate-600 cursor-pointer">
                          Li e aceito a{" "}
                          <a href="#" className="text-sky-600 hover:underline" onClick={(e) => { e.preventDefault(); toast.info("Política de Privacidade do CiaPonto"); }}>
                            Política de Privacidade
                          </a>{" "}
                          e autorizo o tratamento dos meus dados conforme a LGPD *
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation buttons */}
                <div className="flex gap-3 mt-6 pt-4 border-t">
                  {step > 1 && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={prevStep}
                      className="flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Voltar
                    </Button>
                  )}
                  
                  {step < 3 ? (
                    <Button 
                      type="button" 
                      onClick={nextStep}
                      className="flex-1 bg-sky-500 hover:bg-sky-600"
                    >
                      Próximo
                    </Button>
                  ) : (
                    <Button 
                      type="submit" 
                      className="flex-1 bg-green-500 hover:bg-green-600"
                      disabled={loading || !formData.aceite_termos || !formData.aceite_privacidade}
                      data-testid="register-submit-btn"
                    >
                      {loading ? "Criando conta..." : "Criar Conta e Começar"}
                    </Button>
                  )}
                </div>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-slate-600">
                  Já tem uma conta?{" "}
                  <Link to="/login" className="text-sky-600 hover:text-sky-700 font-medium">
                    Fazer login
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Trial info */}
          <div className="mt-4 p-4 bg-sky-50 rounded-lg border border-sky-100 text-center">
            <p className="text-sm text-sky-700">
              <strong>Teste grátis por 7 dias.</strong> Sem necessidade de cartão de crédito.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
